import { useCallback, useEffect, useMemo, useState } from 'react'
import { Check, Loader2, Pencil, RefreshCw, Trash, UserRoundPlus, Users, X } from 'lucide-react'
import { toast } from 'sonner'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  ApiRequestError,
  createInvitation,
  listOrganizationUsers,
  listPendingInvitations,
  removeOrganizationUser,
  resendInvitation,
  revokeInvitation,
  updateOrganizationUserName,
  updateOrganizationUserRole,
  type InvitationSummary,
  type OrgRole,
  type OrganizationUser,
} from '@/lib/api'

interface UsersManagementTabProps {
  currentUserId: string | null
}

export function UsersManagementTab({ currentUserId }: UsersManagementTabProps) {
  const [users, setUsers] = useState<OrganizationUser[]>([])
  const [invitations, setInvitations] = useState<InvitationSummary[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole, setInviteRole] = useState<OrgRole>('member')
  const [lastInviteUrl, setLastInviteUrl] = useState<string | null>(null)
  const [removeUserId, setRemoveUserId] = useState<string | null>(null)
  const [editingNameUserId, setEditingNameUserId] = useState<string | null>(null)
  const [editingNameValue, setEditingNameValue] = useState('')

  const currentUserRole = useMemo(() => {
    const currentUser = users.find((user) => user.id === currentUserId)
    return currentUser?.role ?? null
  }, [users, currentUserId])

  const isAdmin = currentUserRole === 'admin'

  const loadData = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)
      const usersData = await listOrganizationUsers()
      let invitesData: { invitations: InvitationSummary[] } = { invitations: [] }

      // GET /invitations is admin-only. The users response already tells us the
      // caller's role, so skip the request entirely for non-admins rather than
      // firing a guaranteed 403 on every visit (the majority-role navigation).
      const callerIsAdmin = usersData.find((user) => user.id === currentUserId)?.role === 'admin'

      if (callerIsAdmin) {
        try {
          invitesData = await listPendingInvitations()
        } catch (err) {
          // Belt-and-braces: the role could have changed server-side since the
          // users list was built.
          if (!(err instanceof ApiRequestError) || err.code !== 'FORBIDDEN') {
            throw err
          }
        }
      }

      setUsers(usersData)
      setInvitations(invitesData.invitations)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users')
    } finally {
      setIsLoading(false)
    }
  }, [currentUserId])

  useEffect(() => {
    void loadData()
  }, [loadData])

  const handleInvite = async () => {
    if (!isAdmin) {
      return
    }

    const email = inviteEmail.trim()
    if (!email) {
      return
    }

    try {
      setIsSaving(true)
      setError(null)
      const response = await createInvitation(email, inviteRole)
      setInviteEmail('')
      setInviteRole('member')
      setLastInviteUrl(response.inviteUrl ?? null)
      await loadData()
      toast.success('Invitation sent')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to invite user')
    } finally {
      setIsSaving(false)
    }
  }

  const handleRoleChange = async (userId: string, role: OrgRole) => {
    if (!isAdmin) {
      return
    }

    try {
      setError(null)
      const updated = await updateOrganizationUserRole(userId, role)
      setUsers((prev) => prev.map((user) => (user.id === updated.id ? updated : user)))
      toast.success('Role updated')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update role')
    }
  }

  const handleNameEdit = (user: OrganizationUser) => {
    setEditingNameUserId(user.id)
    setEditingNameValue(user.fullName || '')
  }

  const handleNameSave = async (userId: string) => {
    if (!isAdmin) {
      return
    }

    const trimmed = editingNameValue.trim()
    if (!trimmed) {
      return
    }

    try {
      setError(null)
      const updated = await updateOrganizationUserName(userId, trimmed)
      setUsers((prev) => prev.map((user) => (user.id === updated.id ? updated : user)))
      setEditingNameUserId(null)
      toast.success('Name updated')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update name')
    }
  }

  const handleRemoveUser = async (userId: string) => {
    if (!isAdmin) {
      return
    }

    try {
      setError(null)
      await removeOrganizationUser(userId)
      setUsers((prev) => prev.filter((user) => user.id !== userId))
      toast.success('User removed')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove user')
    } finally {
      setRemoveUserId(null)
    }
  }

  const handleResend = async (invitationId: string) => {
    if (!isAdmin) {
      return
    }

    try {
      setError(null)
      const response = await resendInvitation(invitationId)
      setLastInviteUrl(response.inviteUrl ?? null)
      await loadData()
      toast.success('Invitation resent')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to resend invitation')
    }
  }

  const handleRevoke = async (invitationId: string) => {
    if (!isAdmin) {
      return
    }

    try {
      setError(null)
      await revokeInvitation(invitationId)
      setInvitations((prev) => prev.filter((invitation) => invitation.id !== invitationId))
      toast.success('Invitation revoked')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to revoke invitation')
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading users...
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {error}
        </div>
      )}

      {!isAdmin && (
        <div className="rounded-lg border border-border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
          You have view-only access. Only admins can invite users or change roles.
        </div>
      )}

      <section className="space-y-3">
        <h2 className="text-sm font-medium">Invite user</h2>
        <div className="grid gap-3 md:grid-cols-[1fr_180px_auto]">
          <div className="space-y-1">
            <Label htmlFor="invite-email" className="text-meta">Email</Label>
            <Input
              id="invite-email"
              type="email"
              className="h-8"
              value={inviteEmail}
              disabled={!isAdmin || isSaving}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="name@company.com"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-meta">Role</Label>
            <Select
              value={inviteRole}
              onValueChange={(value) => setInviteRole(value as OrgRole)}
              disabled={!isAdmin || isSaving}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="member">Member</SelectItem>
                <SelectItem value="viewer">Viewer</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <Button size="sm" onClick={() => void handleInvite()} disabled={!isAdmin || isSaving || !inviteEmail.trim()}>
              {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserRoundPlus className="h-4 w-4" />}
              Invite
            </Button>
          </div>
        </div>
        {lastInviteUrl && (
          <p className="text-meta">
            Dev fallback invite URL: <code className="rounded bg-muted px-1 py-0.5">{lastInviteUrl}</code>
          </p>
        )}
      </section>

      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-medium">Organisation users</h2>
          <Button variant="ghost" size="sm" onClick={() => void loadData()}>
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
        </div>
        <div className="bg-card rounded-lg border overflow-x-auto">
          <div className="min-w-[520px]">
            <div className="grid grid-cols-[1.2fr_1fr_140px_120px] gap-3 border-b bg-muted/30 px-3 py-3 text-xs font-medium text-muted-foreground">
              <span>Name</span>
              <span>Email</span>
              <span>Role</span>
              <span>Actions</span>
            </div>
            {users.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <Users className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">No users in this organisation yet.</p>
              </div>
            ) : (
              users.map((user) => (
                <div key={user.id} className="grid grid-cols-[1.2fr_1fr_140px_120px] items-center gap-3 px-3 py-2 text-sm border-b last:border-b-0">
                  {editingNameUserId === user.id ? (
                    <div className="flex items-center gap-1">
                      <Input
                        className="h-7 text-sm"
                        value={editingNameValue}
                        onChange={(e) => setEditingNameValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') void handleNameSave(user.id)
                          if (e.key === 'Escape') setEditingNameUserId(null)
                        }}
                        autoFocus
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 shrink-0"
                        disabled={!editingNameValue.trim()}
                        onClick={() => void handleNameSave(user.id)}
                      >
                        <Check className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 shrink-0"
                        onClick={() => setEditingNameUserId(null)}
                      >
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ) : (
                    <span className="flex items-center gap-1 group">
                      {user.fullName || 'No name'}
                      {isAdmin && (
                        <button
                          className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-foreground"
                          onClick={() => handleNameEdit(user)}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </span>
                  )}
                  <span className="truncate text-muted-foreground">{user.email}</span>
                  <Select
                    value={user.role}
                    disabled={!isAdmin || user.id === currentUserId}
                    onValueChange={(value) => void handleRoleChange(user.id, value as OrgRole)}
                  >
                    <SelectTrigger className="h-8">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Admin</SelectItem>
                      <SelectItem value="member">Member</SelectItem>
                      <SelectItem value="viewer">Viewer</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                    disabled={!isAdmin || user.id === currentUserId}
                    onClick={() => setRemoveUserId(user.id)}
                  >
                    <Trash className="h-4 w-4" />
                    Remove
                  </Button>
                </div>
              ))
            )}
          </div>
        </div>
      </section>

      <section className="space-y-3">
        <h2 className="text-sm font-medium">Pending invitations</h2>
        {!isAdmin ? (
          // An empty table here would read as "no invitations exist" when the
          // truth is that this user was never allowed to look.
          <p className="text-sm text-muted-foreground">
            Only admins can view or manage pending invitations.
          </p>
        ) : (
        <div className="bg-card rounded-lg border overflow-x-auto">
          <div className="min-w-[520px]">
            <div className="grid grid-cols-[1fr_120px_180px_170px] gap-3 border-b bg-muted/30 px-3 py-3 text-xs font-medium text-muted-foreground">
              <span>Email</span>
              <span>Role</span>
              <span>Expires</span>
              <span>Actions</span>
            </div>
            {invitations.length === 0 ? (
              <div className="px-3 py-4 text-sm text-muted-foreground">No pending invitations.</div>
            ) : (
              invitations.map((invitation) => (
                <div key={invitation.id} className="grid grid-cols-[1fr_120px_180px_170px] items-center gap-3 px-3 py-2 text-sm border-b last:border-b-0">
                  <span>{invitation.email}</span>
                  <span className="capitalize">{invitation.role}</span>
                  <span className="text-muted-foreground">{new Date(invitation.expiresAt).toLocaleString()}</span>
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={!isAdmin}
                      onClick={() => void handleResend(invitation.id)}
                    >
                      Resend
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                      disabled={!isAdmin}
                      onClick={() => void handleRevoke(invitation.id)}
                    >
                      Revoke
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
        )}
      </section>

      <AlertDialog open={removeUserId !== null} onOpenChange={(open) => { if (!open) setRemoveUserId(null) }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove user</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the user from the organisation. They will lose access to all shared resources.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => { if (removeUserId) void handleRemoveUser(removeUserId) }}
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
